import { z } from 'zod';
import { DateTimeFormatter, DayOfWeek, Duration, Instant, LocalDate, LocalTime, OffsetDateTime, Period, ZoneId, ZoneOffset, convert, nativeJs, } from '@js-joda/core';
import { Interval } from '@js-joda/extra';
/**
 * DB-tolerant optional. Accepts the inner value, `null`, or absent (`undefined`)
 * on the wire/DB side; always normalises to `T | undefined` on the JS side.
 *
 * Use this for any DB-stored field whose JS-side semantic is "optional" but
 * whose DB documents may have an explicit `null` (often left over from earlier
 * writes that wrote `null` instead of omitting the field). Pair with the
 * `ignoreUndefined: true` MongoClient option so writes round-trip cleanly:
 * `T | undefined` JS-side → `T` or omitted in BSON, never `null`.
 *
 * @example
 * const Doc = z.object({
 *   description: dbOptional(z.string()),  // DB: string | null | absent → JS: string | undefined
 *   count: dbOptional(z.number()),
 * });
 */
function isCodec(schema) {
    return schema instanceof z.ZodCodec;
}
export function dbOptional(schema) {
    // `z.codec` runs three stages per direction — `in -> transform -> out` when decoding, and
    // `out -> reverseTransform -> in` when encoding. `schema` must therefore be referenced by
    // exactly one of those stages, or the later stages receive the earlier stage's *output*
    // instead of the original input: that is outright wrong for a nested codec whose output
    // type differs from its input type (`InstantDateCodec` decodes `Date` to `Instant`, and
    // cannot then take an `Instant`), and multiplies the work by 3 per level of nesting for
    // the ones that happen to round-trip. So `schema` lives only in `in`, which serves both
    // directions: forwards it parses the stored value, backwards it re-encodes the JS value.
    //
    // `out` is deliberately `z.any()`. It cannot usefully validate anything the transform
    // could produce — the transform only ever forwards what `in` already accepted, or
    // `undefined` — and the overloads above carry the real output type for callers.
    // `toMongoJsonSchema` reads the input side (`z.toJSONSchema(schema, { io: 'input' })`),
    // so validator generation is unaffected.
    //
    // `.nullable()` rather than `z.union([schema, z.null()])`: `ZodNullable` returns
    // immediately on `null`, whereas a union runs `schema` first and has to fail it.
    //
    // Both hooks collapse `null` to `undefined`, in both directions. `in` accepts `null` so
    // that legacy documents decode, and with `out` as `z.any()` nothing else rejects it on the
    // way back — so if `encode` forwarded `null`, a `null` reaching the helper from untyped
    // code would be written straight back to Mongo, which is the one thing `dbOptional` exists
    // to prevent.
    if (isCodec(schema)) {
        return z.codec(schema.in.nullable().optional(), z.any(), {
            // Decode: collapse explicit `null` (legacy DB writes) into `undefined`; preserve T.
            // Encode: emit T or `undefined` (omitted in BSON), never `null`.
            decode: (val) => (val == null ? undefined : schema.decode(val)),
            encode: (val) => (val == null ? undefined : schema.encode(val)),
        });
    }
    else {
        return z.codec(schema.nullable().optional(), z.any(), {
            // Decode: collapse explicit `null` (legacy DB writes) into `undefined`; preserve T.
            // Encode: emit T or `undefined` (omitted in BSON), never `null`. `in`'s backward
            // pass re-applies any nested codecs, so neither hook has to touch the value itself.
            decode: (val) => val ?? undefined,
            encode: (val) => val ?? undefined,
        });
    }
}
/**
 * Wire format schema for ISO 8601 datetime with timezone offset.
 * Use this when you only need the string validation without codec transformation.
 */
export const InstantStringSchema = z.iso.datetime({ offset: true });
/**
 * Wire format schema for ISO 8601 duration.
 * Use this when you only need the string validation without codec transformation.
 */
export const DurationStringSchema = z.iso.duration();
/**
 * Wire format schema for ISO 8601 period.
 * Use this when you only need the string validation without codec transformation.
 */
export const PeriodStringSchema = z
    .string()
    .regex(/^P(?:\d+Y)?(?:\d+M)?(?:\d+D)?(?:T(?:\d+H|\d+M|\d+(?:\.\d+)?S)(?:\d+H)?(?:\d+M)?(?:\d+(?:\.\d+)?S)?)?$/, {
    message: 'Invalid ISO 8601 period format',
});
/**
 * Custom Zod type for runtime Instant validation.
 * Use this when you need to validate that a value is already an Instant object.
 */
export const ZodInstant = z.custom((val) => val instanceof Instant, {
    message: 'Expected Instant',
});
/**
 * Custom Zod type for runtime LocalDate validation.
 * Use this when you need to validate that a value is already a LocalDate object.
 */
export const ZodLocalDate = z.custom((val) => val instanceof LocalDate, {
    message: 'Expected LocalDate',
});
/**
 * Custom Zod type for runtime LocalTime validation.
 * Use this when you need to validate that a value is already a LocalTime object.
 */
export const ZodLocalTime = z.custom((val) => val instanceof LocalTime, {
    message: 'Expected LocalTime',
});
/**
 * Custom Zod type for runtime Duration validation.
 * Use this when you need to validate that a value is already a Duration object.
 */
export const ZodDuration = z.custom((val) => val instanceof Duration, {
    message: 'Expected Duration',
});
/**
 * Custom Zod type for runtime Period validation.
 * Use this when you need to validate that a value is already a Period object.
 */
export const ZodPeriod = z.custom((val) => val instanceof Period, {
    message: 'Expected Period',
});
/**
 * Custom Zod type for runtime ZoneId validation.
 * Use this when you need to validate that a value is already a ZoneId object.
 */
export const ZodZoneId = z.custom((val) => val instanceof ZoneId, {
    message: 'Expected ZoneId',
});
/**
 * Custom Zod type for runtime DayOfWeek validation.
 * Use this when you need to validate that a value is already a DayOfWeek object.
 */
export const ZodDayOfWeek = z.custom((val) => val instanceof DayOfWeek, {
    message: 'Expected DayOfWeek',
});
/**
 * Custom Zod type for runtime Interval validation.
 * Use this when you need to validate that a value is already an Interval object.
 */
export const ZodInterval = z.custom((val) => val instanceof Interval, {
    message: 'Expected Interval',
});
/**
 * Bidirectional codec for Instant <-> ISO 8601 datetime string.
 *
 * Input: ISO 8601 datetime string (e.g., '2024-01-15T10:30:00Z')
 * Output: js-joda Instant object
 *
 * @example
 * // Decode from string to Instant
 * InstantCodec.decode('2024-01-15T10:30:00Z')  // -> Instant
 *
 * // Encode from Instant to string
 * InstantCodec.encode(instant)  // -> '2024-01-15T10:30:00.000Z'
 *
 * // Parse unknown value (validates, then decodes)
 * InstantCodec.parse('2024-01-15T10:30:00Z')  // -> Instant
 */
export const InstantCodec = z
    .codec(InstantStringSchema, ZodInstant, {
    decode: (isoString) => {
        const odt = OffsetDateTime.parse(isoString, DateTimeFormatter.ISO_OFFSET_DATE_TIME);
        return odt.toInstant();
    },
    encode: (instant) => instant.toString(),
})
    .meta({ id: 'Instant' });
/**
 * Bidirectional codec for Instant <-> JavaScript Date (for MongoDB storage).
 *
 * Input: JavaScript Date object (stored as BSON date in MongoDB)
 * Output: js-joda Instant object
 *
 * Note: JavaScript Date has millisecond precision, so nanosecond data will be lost.
 * Use this codec for MongoDB storage where native BSON dates provide efficient
 * date range queries.
 *
 * @example
 * // Decode from Date to Instant
 * InstantDateCodec.decode(new Date('2024-01-15T10:30:00Z'))  // -> Instant
 *
 * // Encode from Instant to Date
 * InstantDateCodec.encode(instant)  // -> Date
 *
 * // Parse unknown value (validates, then decodes)
 * InstantDateCodec.parse(new Date())  // -> Instant
 */
export const InstantDateCodec = z
    .codec(z.date(), ZodInstant, {
    decode: (date) => nativeJs(date).toInstant(),
    encode: (instant) => convert(instant).toDate(),
})
    .meta({ id: 'InstantDate' });
/**
 * Bidirectional codec for Instant <-> Unix epoch seconds.
 *
 * Input: integer seconds since 1970-01-01T00:00:00Z
 * Output: js-joda Instant object
 *
 * @example
 * // Decode from epoch seconds to Instant
 * EpochSecondsInstantCodec.decode(1715769000)  // -> Instant
 *
 * // Encode from Instant to epoch seconds
 * EpochSecondsInstantCodec.encode(instant)  // -> 1715769000
 */
export const EpochSecondsInstantCodec = z
    .codec(z.int(), ZodInstant, {
    decode: (epochSeconds) => Instant.ofEpochSecond(epochSeconds),
    encode: (instant) => instant.epochSecond(),
})
    .meta({ id: 'EpochSecondsInstant' });
/**
 * Bidirectional codec for LocalDate <-> ISO 8601 date string.
 *
 * Input: ISO 8601 date string (YYYY-MM-DD format, e.g., '2024-01-15')
 * Output: js-joda LocalDate object
 *
 * @example
 * // Decode from string to LocalDate
 * LocalDateCodec.decode('2024-01-15')  // -> LocalDate
 *
 * // Encode from LocalDate to string
 * LocalDateCodec.encode(localDate)  // -> '2024-01-15'
 *
 * // Parse unknown value (validates, then decodes)
 * LocalDateCodec.parse('2024-01-15')  // -> LocalDate
 */
export const LocalDateCodec = z
    .codec(z.iso.date(), ZodLocalDate, {
    decode: (dateString) => LocalDate.parse(dateString),
    encode: (localDate) => localDate.toString(),
})
    .meta({ id: 'LocalDate' });
/**
 * Convert a numeric YYYYMMDD "dint" value to a LocalDate.
 */
export function dintToLocalDate(date) {
    const year = Math.floor(date / 10000);
    const month = Math.floor((date % 10000) / 100);
    const day = date % 100;
    return LocalDate.of(year, month, day);
}
/**
 * Convert a LocalDate to a numeric YYYYMMDD "dint" value.
 */
export function localDateToDint(date) {
    return date.year() * 10000 + date.monthValue() * 100 + date.dayOfMonth();
}
/**
 * Convert a numeric YYYYMMDD "dint" value to a start-of-day Instant in the given zone.
 */
export function dintToInstant(date, zone = ZoneOffset.UTC) {
    return dintToLocalDate(date).atStartOfDay(zone).toInstant();
}
/**
 * Convert an Instant to the LocalDate containing it in the given zone.
 */
export function instantToDintLocalDate(instant, zone = ZoneOffset.UTC) {
    return instant.atZone(zone).toLocalDate();
}
/**
 * Convert an Instant to a numeric YYYYMMDD "dint" value in the given zone.
 */
export function instantToDint(instant, zone = ZoneOffset.UTC) {
    return localDateToDint(instantToDintLocalDate(instant, zone));
}
/**
 * Bidirectional codec for LocalDate <-> numeric YYYYMMDD "dint" values.
 *
 * Input: integer in YYYYMMDD format (e.g., 20240115)
 * Output: js-joda LocalDate object
 *
 * @example
 * // Decode from number to LocalDate
 * DintLocalDateCodec.decode(20240115)  // -> LocalDate
 *
 * // Encode from LocalDate to number
 * DintLocalDateCodec.encode(localDate)  // -> 20240115
 */
export const DintLocalDateCodec = z
    .codec(z.int(), ZodLocalDate, {
    decode: dintToLocalDate,
    encode: localDateToDint,
})
    .meta({ id: 'DintLocalDate' });
/**
 * Build a bidirectional codec for Instant <-> numeric YYYYMMDD "dint" values.
 *
 * Dints do not carry timezone data; decode treats each value as start of day in `zone`,
 * and encode derives the calendar date that contains the Instant in `zone`.
 */
export function createDintInstantCodec(zone = ZoneOffset.UTC) {
    return z.codec(z.int(), ZodInstant, {
        decode: (date) => dintToInstant(date, zone),
        encode: (instant) => instantToDint(instant, zone),
    });
}
/**
 * Bidirectional codec for Instant <-> numeric YYYYMMDD "dint" values in UTC.
 */
export const DintInstantCodec = createDintInstantCodec().meta({ id: 'DintInstant' });
/**
 * Bidirectional codec for LocalTime <-> ISO 8601 time string.
 *
 * Input: ISO 8601 time string (HH:mm:ss format, e.g., '10:30:00' or '10:30:00.123')
 * Output: js-joda LocalTime object
 *
 * @example
 * // Decode from string to LocalTime
 * LocalTimeCodec.decode('10:30:00')  // -> LocalTime
 *
 * // Encode from LocalTime to string
 * LocalTimeCodec.encode(localTime)  // -> '10:30:00'
 *
 * // Parse unknown value (validates, then decodes)
 * LocalTimeCodec.parse('10:30:00')  // -> LocalTime
 */
export const LocalTimeCodec = z
    .codec(z.iso.time(), ZodLocalTime, {
    decode: (timeString) => LocalTime.parse(timeString),
    encode: (localTime) => localTime.format(DateTimeFormatter.ISO_TIME),
})
    .meta({ id: 'LocalTime' });
/**
 * Bidirectional codec for Duration <-> ISO 8601 duration string.
 *
 * Input: ISO 8601 duration string (e.g., 'PT1H30M', 'P1D', 'P1DT12H')
 * Output: js-joda Duration object
 *
 * @example
 * // Decode from string to Duration
 * DurationCodec.decode('PT1H30M')  // -> Duration of 1 hour 30 minutes
 *
 * // Encode from Duration to string
 * DurationCodec.encode(duration)  // -> 'PT1H30M'
 *
 * // Parse unknown value (validates, then decodes)
 * DurationCodec.parse('P1D')  // -> Duration of 1 day
 */
export const DurationCodec = z
    .codec(DurationStringSchema, ZodDuration, {
    decode: (durationString) => Duration.parse(durationString),
    encode: (duration) => duration.toString(),
})
    .meta({ id: 'Duration' });
/**
 * Bidirectional codec for Period <-> ISO 8601 period string.
 *
 * Input: ISO 8601 period string (e.g., 'P1Y2M3D', 'P1Y', 'P6M')
 * Output: js-joda Period object
 *
 * @example
 * // Decode from string to Period
 * PeriodCodec.decode('P1Y2M3D')  // -> Period of 1 year, 2 months, 3 days
 *
 * // Encode from Period to string
 * PeriodCodec.encode(period)  // -> 'P1Y2M3D'
 *
 * // Parse unknown value (validates, then decodes)
 * PeriodCodec.parse('P6M')  // -> Period of 6 months
 */
export const PeriodCodec = z
    .codec(PeriodStringSchema, ZodPeriod, {
    decode: (periodString) => Period.parse(periodString),
    encode: (period) => period.toString(),
})
    .meta({ id: 'Period' });
/**
 * Bidirectional codec for ZoneId <-> timezone string.
 *
 * Input: IANA timezone string (e.g., 'America/New_York', 'UTC', 'Europe/London')
 * Output: js-joda ZoneId object
 *
 * @example
 * // Decode from string to ZoneId
 * ZoneIdCodec.decode('America/New_York')  // -> ZoneId
 *
 * // Encode from ZoneId to string
 * ZoneIdCodec.encode(zoneId)  // -> 'America/New_York'
 *
 * // Parse unknown value (validates, then decodes)
 * ZoneIdCodec.parse('Europe/London')  // -> ZoneId
 */
export const ZoneIdCodec = z
    .codec(z.string(), ZodZoneId, {
    decode: (zoneIdString) => ZoneId.of(zoneIdString),
    encode: (zoneId) => zoneId.toString(),
})
    .meta({ id: 'ZoneId' });
/**
 * Bidirectional codec for DayOfWeek <-> day name string.
 *
 * Input: Day of week name (e.g., 'MONDAY', 'TUESDAY', 'SUNDAY')
 * Output: js-joda DayOfWeek object
 *
 * @example
 * // Decode from string to DayOfWeek
 * DayOfWeekCodec.decode('MONDAY')  // -> DayOfWeek.MONDAY
 *
 * // Encode from DayOfWeek to string
 * DayOfWeekCodec.encode(DayOfWeek.MONDAY)  // -> 'MONDAY'
 *
 * // Parse unknown value (validates, then decodes)
 * DayOfWeekCodec.parse('FRIDAY')  // -> DayOfWeek.FRIDAY
 */
const DayOfWeekNames = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
export const DayOfWeekCodec = z
    .codec(z.enum(DayOfWeekNames), ZodDayOfWeek, {
    decode: (dayString) => DayOfWeek.valueOf(dayString),
    encode: (dayOfWeek) => dayOfWeek.name(),
})
    .meta({ id: 'DayOfWeek' });
/**
 * Bidirectional codec for Interval <-> ISO 8601 interval string.
 *
 * Input: ISO 8601 interval string in format '<start>/<end>' (e.g., '2024-01-15T10:30:00Z/2024-01-16T10:30:00Z')
 * Output: js-joda Interval object
 *
 * @example
 * // Decode from string to Interval
 * IntervalCodec.decode('2024-01-15T10:30:00Z/2024-01-16T10:30:00Z')  // -> Interval
 *
 * // Encode from Interval to string
 * IntervalCodec.encode(interval)  // -> '2024-01-15T10:30:00Z/2024-01-16T10:30:00Z'
 *
 * // Parse unknown value (validates, then decodes)
 * IntervalCodec.parse('2024-01-15T10:30:00Z/2024-01-16T10:30:00Z')  // -> Interval
 */
export const IntervalCodec = z
    .codec(z.string(), ZodInterval, {
    decode: (intervalString) => {
        return Interval.parse(intervalString);
    },
    encode: (interval) => {
        return `${interval.start().toString()}/${interval.end().toString()}`;
    },
})
    .meta({ id: 'Interval' });
/**
 * Aliases for backwards compatibility with contracts that use Schema naming
 */
export { InstantCodec as InstantSchema };
export { DurationCodec as DurationSchema };
/**
 * Re-export js-joda types for consistent type resolution across the monorepo.
 * Import these from '@thinkalpha/common/schemas/codecs.js' instead of '@js-joda/core'
 * to avoid TS2742 portability errors in declaration emit.
 */
export { Instant, LocalDate, LocalTime, Duration, Period, ZoneId, DayOfWeek, Interval };
//# sourceMappingURL=codecs.js.map